import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Shield, FileText, FileCheck, CheckCircle2, ArrowRight, Lock, AlertCircle } from 'lucide-react';
import AEDIcon from './icons/AEDIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/context/AuthContext';
import api from '@/services/api';

const STEPS = [
  { id: 'kyc', label: 'Identity Verification', icon: FileText, description: 'Verify your Emirates ID or Passport' },
  { id: 'financial', label: 'Financial Verification', icon: AEDIcon, description: 'Proof of funds or mortgage pre-approval' },
  { id: 'agreements', label: 'Platform Agreements', icon: FileCheck, description: 'Accept terms to access intelligence' },
];

const AGREEMENTS = [
  {
    type: 'non_circumvention',
    title: 'Non-Circumvention Agreement',
    description: 'I agree not to contact property owners directly outside the Rashii platform.'
  },
  {
    type: 'confidentiality',
    title: 'Confidentiality Agreement',
    description: 'I agree to keep all property and seller information confidential.'
  },
  {
    type: 'commission',
    title: 'Commission Acknowledgement',
    description: 'I acknowledge and accept the standard commission structure for transactions.'
  }
];

export const VerifiedInterestFlow = ({ propertyId, onComplete, onClose }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [currentStep, setCurrentStep] = useState('kyc');
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // KYC form state
  const [kycData, setKycData] = useState({
    document_type: 'emirates_id',
    document_number: '',
    expiry_date: ''
  });
  
  // Financial form state
  const [financialData, setFinancialData] = useState({
    bank_name: '',
    account_type: 'savings',
    available_balance: '',
    currency: 'AED'
  });
  
  // Agreements state
  const [acceptedAgreements, setAcceptedAgreements] = useState({
    non_circumvention: false,
    confidentiality: false,
    commission: false
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=/property/${propertyId}`);
      return;
    }
    fetchVerificationStatus();
  }, [isAuthenticated, propertyId]);

  const fetchVerificationStatus = async () => {
    try {
      const response = await api.get('/buyer/verification-status');
      setVerificationStatus(response.data);
      setCurrentStep(response.data.current_step);
      
      if (response.data.is_fully_verified) {
        // User is already verified, unlock property
        await unlockProperty();
      }
    } catch (error) {
      console.error('Failed to fetch verification status:', error);
    } finally {
      setLoading(false);
    }
  };

  const unlockProperty = async () => {
    try {
      const response = await api.post(`/properties/${propertyId}/unlock`);
      if (onComplete) {
        onComplete(response.data);
      }
    } catch (error) {
      console.error('Failed to unlock property:', error);
    }
  };

  const handleKYCSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/kyc/submit', kycData);
      await fetchVerificationStatus();
      setCurrentStep('financial');
    } catch (error) {
      console.error('KYC submission failed:', error);
    }
    setSubmitting(false);
  };

  const handleFinancialSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/kyc/proof-of-funds', {
        ...financialData,
        available_balance: parseFloat(financialData.available_balance)
      });
      await fetchVerificationStatus();
      setCurrentStep('agreements');
    } catch (error) {
      console.error('Financial verification failed:', error);
    }
    setSubmitting(false);
  };

  const handleAgreementsSubmit = async () => {
    // Check if all agreements are accepted
    if (!Object.values(acceptedAgreements).every(Boolean)) {
      return;
    }
    
    setSubmitting(true);
    try {
      const agreements = Object.entries(acceptedAgreements)
        .filter(([_, accepted]) => accepted)
        .map(([type, _]) => ({ agreement_type: type, accepted: true }));
      
      await api.post('/buyer/agreements', agreements);
      await unlockProperty();
    } catch (error) {
      console.error('Agreements submission failed:', error);
    }
    setSubmitting(false);
  };

  const getStepIndex = () => {
    const stepOrder = ['kyc', 'financial', 'agreements', 'completed'];
    return stepOrder.indexOf(currentStep);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="animate-pulse text-primary">Loading verification status...</div>
      </div>
    );
  }

  if (verificationStatus?.is_fully_verified) {
    return null; // Already verified, property should be unlocking
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="verified-interest-flow">
      <Card className="w-full max-w-lg glass-card border-white/10 gold-glow">
        <CardHeader className="text-center pb-4">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-primary" />
            <span className="text-xs uppercase tracking-[0.2em] text-primary">Verified Access</span>
          </div>
          <CardTitle className="font-display text-2xl">Unlock Property Intelligence</CardTitle>
          <CardDescription className="text-muted-foreground">
            To protect our sellers and maintain serious transaction standards, access to detailed property intelligence requires verification.
          </CardDescription>
          
          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex justify-between text-xs text-muted-foreground mb-2">
              <span>Verification Progress</span>
              <span>{verificationStatus?.completed_steps || 1}/4 Complete</span>
            </div>
            <Progress value={((verificationStatus?.completed_steps || 1) / 4) * 100} className="h-2" />
          </div>
          
          {/* Step Indicators */}
          <div className="flex justify-between mt-4">
            {STEPS.map((step, idx) => {
              const isCompleted = getStepIndex() > idx;
              const isCurrent = currentStep === step.id;
              return (
                <div key={step.id} className={`flex flex-col items-center ${isCurrent ? 'text-primary' : isCompleted ? 'text-green-500' : 'text-muted-foreground'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                    isCompleted ? 'bg-green-600 border-green-600' : isCurrent ? 'border-primary bg-primary/10' : 'border-muted'
                  }`}>
                    {isCompleted ? <CheckCircle2 className="w-4 h-4 text-white" /> : <step.icon className="w-4 h-4" />}
                  </div>
                  <span className="text-[10px] mt-1 uppercase tracking-wider">{step.label.split(' ')[0]}</span>
                </div>
              );
            })}
          </div>
        </CardHeader>
        
        <CardContent>
          {/* KYC Step */}
          {currentStep === 'kyc' && (
            <form onSubmit={handleKYCSubmit} className="space-y-4">
              <div className="p-3 rounded bg-primary/10 border border-primary/30 text-sm">
                <Shield className="w-4 h-4 text-primary inline mr-2" />
                Your data is verified but never stored. Privacy guaranteed.
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Document Type</Label>
                <Select value={kycData.document_type} onValueChange={(v) => setKycData({...kycData, document_type: v})}>
                  <SelectTrigger className="bg-transparent border-white/20" data-testid="kyc-doc-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="emirates_id">Emirates ID</SelectItem>
                    <SelectItem value="passport">Passport</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Document Number</Label>
                <Input
                  placeholder="784-XXXX-XXXXXXX-X"
                  value={kycData.document_number}
                  onChange={(e) => setKycData({...kycData, document_number: e.target.value})}
                  className="bg-transparent border-white/20"
                  required
                  data-testid="kyc-doc-number"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Expiry Date</Label>
                <Input
                  type="date"
                  value={kycData.expiry_date}
                  onChange={(e) => setKycData({...kycData, expiry_date: e.target.value})}
                  className="bg-transparent border-white/20"
                  data-testid="kyc-expiry"
                />
              </div>
              
              <Button type="submit" className="w-full bg-primary text-primary-foreground" disabled={submitting} data-testid="kyc-submit">
                {submitting ? 'Verifying...' : 'Verify Identity'} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          )}
          
          {/* Financial Step */}
          {currentStep === 'financial' && (
            <form onSubmit={handleFinancialSubmit} className="space-y-4">
              <div className="p-3 rounded bg-primary/10 border border-primary/30 text-sm">
                <AEDIcon className="w-4 h-4 text-primary inline mr-2" />
                Proof of funds ensures all buyers are qualified and ready to transact.
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Bank Name</Label>
                <Input
                  placeholder="Emirates NBD"
                  value={financialData.bank_name}
                  onChange={(e) => setFinancialData({...financialData, bank_name: e.target.value})}
                  className="bg-transparent border-white/20"
                  required
                  data-testid="pof-bank"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Account Type</Label>
                <Select value={financialData.account_type} onValueChange={(v) => setFinancialData({...financialData, account_type: v})}>
                  <SelectTrigger className="bg-transparent border-white/20" data-testid="pof-account-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="savings">Savings Account</SelectItem>
                    <SelectItem value="current">Current Account</SelectItem>
                    <SelectItem value="investment">Investment Account</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Available Balance (AED)</Label>
                <Input
                  type="number"
                  placeholder="5,000,000"
                  value={financialData.available_balance}
                  onChange={(e) => setFinancialData({...financialData, available_balance: e.target.value})}
                  className="bg-transparent border-white/20"
                  required
                  data-testid="pof-balance"
                />
              </div>
              
              <Button type="submit" className="w-full bg-primary text-primary-foreground" disabled={submitting} data-testid="pof-submit">
                {submitting ? 'Verifying...' : 'Verify Funds'} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          )}
          
          {/* Agreements Step */}
          {currentStep === 'agreements' && (
            <div className="space-y-4">
              <div className="p-3 rounded bg-primary/10 border border-primary/30 text-sm">
                <FileCheck className="w-4 h-4 text-primary inline mr-2" />
                Accept these agreements to access full property intelligence.
              </div>
              
              <div className="space-y-4">
                {AGREEMENTS.map((agreement) => (
                  <div 
                    key={agreement.type}
                    className={`p-4 rounded border transition-colors ${
                      acceptedAgreements[agreement.type] ? 'border-primary bg-primary/5' : 'border-white/10'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id={agreement.type}
                        checked={acceptedAgreements[agreement.type]}
                        onCheckedChange={(checked) => setAcceptedAgreements({
                          ...acceptedAgreements,
                          [agreement.type]: checked
                        })}
                        className="mt-1"
                        data-testid={`agreement-${agreement.type}`}
                      />
                      <div>
                        <Label htmlFor={agreement.type} className="font-medium cursor-pointer">
                          {agreement.title}
                        </Label>
                        <p className="text-xs text-muted-foreground mt-1">{agreement.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <Button 
                className="w-full bg-primary text-primary-foreground" 
                disabled={!Object.values(acceptedAgreements).every(Boolean) || submitting}
                onClick={handleAgreementsSubmit}
                data-testid="agreements-submit"
              >
                {submitting ? 'Processing...' : 'Unlock Property'} <CheckCircle2 className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
          
          {/* Close/Skip button */}
          <Button 
            variant="ghost" 
            className="w-full mt-4 text-muted-foreground"
            onClick={onClose}
          >
            Cancel
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifiedInterestFlow;
